import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

/**
 * Posao 31: tema se bira u korisničkom izborniku — Tamna, Svijetla ili Sustav (prati uređaj).
 * Posao 32: tko temu nikad nije birao dobiva Tamnu; spremljeni izbor (i "Sustav") se poštuje.
 */
export type ThemeMode = 'dark' | 'light' | 'system';

interface ThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const DARK_QUERY = '(prefers-color-scheme: dark)';
const systemDark = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;

function readMode(): ThemeMode {
  try {
    const saved = localStorage.getItem('theme');
    if (saved === 'dark' || saved === 'light' || saved === 'system') return saved;
  } catch { /* privatni prozor */ }
  return 'dark';
}

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(readMode);
  const [systemIsDark, setSystemIsDark] = useState(systemDark);

  // Sprema se samo izričit odabir, pa zadana tema ne postaje "odabrana" sama od sebe.
  const setMode = (next: ThemeMode) => {
    setModeState(next);
    try { localStorage.setItem('theme', next); } catch { /* privatni prozor */ }
  };

  // Za "Sustav" tema prati promjenu na uređaju bez ponovnog učitavanja.
  useEffect(() => {
    if (mode !== 'system' || typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia(DARK_QUERY);
    const onChange = () => setSystemIsDark(media.matches);
    onChange();
    media.addEventListener?.('change', onChange);
    return () => media.removeEventListener?.('change', onChange);
  }, [mode]);

  const isDark = mode === 'system' ? systemIsDark : mode === 'dark';
  const toggleTheme = () => setMode(isDark ? 'light' : 'dark');

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme, mode, setMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
